/**
 * Reward Create Page
 *
 * Create a new reward catalog item with all fields.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select, TextArea } from '@/components/common/FormField';
import { SelectWithAdd } from '@/components/common/FormField/SelectWithAdd';
import {
  CreateRewardRequest,
  WorkflowContent,
  RedemptionConfig,
  RewardConfig,
  STATUS_OPTIONS,
  RewardCategory,
  RewardStatus,
  RewardVariant,
  WorkflowType,
  RewardResponse,
} from '@/app/types/rewards';
import { rewardService } from '@/app/services/rewardService';
import { auditService } from '@/app/services';
import { UIContentEditor } from '@/components/rewards/UIContentEditor';
import { RedemptionConfigEditor } from '@/components/rewards/RedemptionConfigEditor';
import { RewardConfigEditor } from '@/components/rewards/RewardConfigEditor';
import {
  useRewardMetadata,
  getCategoryOptions,
  getVariantOptions,
} from '@/app/hooks/useRewardMetadata';
import { useRewardData } from '@/app/hooks/useRewardData';

import '../RewardEdit/RewardEdit.scss'; // Reuse edit styles

const WORKFLOW_OPTIONS = [
  { value: '', label: 'Select workflow type' },
  { value: 'FIXED_AMOUNT', label: 'Fixed Amount' },
  { value: 'VARIABLE_AMOUNT', label: 'Variable Amount' },
];

// Default values for new reward
const DEFAULT_FORM_DATA: CreateRewardRequest = {
  reward_key: '',
  reward_name: '',
  ux_reward_title: '',
  ux_reward_subtitle: null,
  ux_reward_description: null,
  ux_image_url: null,
  category: 'reward cards',
  status: 'DRAFT',
  point_cost: 0,
  dollar_value: null,
  out_of_pocket_cost: null,
  ui_content_json: null,
  redemption_config: null,
  reward_variant: 'default',
  provider_name: null,
};

export const RewardCreate: React.FC = () => {
  const navigate = useNavigate();
  const { categories, variants, loading: metadataLoading } = useRewardMetadata();
  const { invalidate, refresh } = useRewardData();

  // Local state for newly added categories/variants/providers
  const [localCategories, setLocalCategories] = useState<string[]>([]);
  const [localVariants, setLocalVariants] = useState<string[]>([]);
  const [localProviders, setLocalProviders] = useState<string[]>([]);

  // Merge fetched categories/variants with locally added ones
  const allCategories = [...categories, ...localCategories];
  const allVariants = [...variants, ...localVariants];

  const categoryOptions = getCategoryOptions(allCategories);
  const variantOptions = getVariantOptions(allVariants);

  // State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateRewardRequest>(DEFAULT_FORM_DATA);

  // Copy-from feature state
  const [availableRewards, setAvailableRewards] = useState<RewardResponse[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [selectedCopyFrom, setSelectedCopyFrom] = useState<string>('');

  // Fetch available rewards for copy-from dropdown
  useEffect(() => {
    const fetchRewards = async () => {
      setLoadingRewards(true);
      try {
        const response = await rewardService.queryRewards({ limit: 100 });
        setAvailableRewards(response.rewards);
      } catch (err) {
        logger.error('Failed to fetch rewards for copy-from', { error: err });
      } finally {
        setLoadingRewards(false);
      }
    };
    fetchRewards();
  }, []);

  // Derive unique providers from available rewards
  const providerOptions = useMemo(() => {
    const providersFromRewards = availableRewards
      .map((r) => r.provider_name)
      .filter((p): p is string => !!p);
    const allProviders = [...new Set([...providersFromRewards, ...localProviders])].sort();
    return allProviders.map((provider) => ({
      value: provider,
      label: provider,
    }));
  }, [availableRewards, localProviders]);

  // Handle copying from an existing reward
  const handleCopyFrom = useCallback(async (rewardId: string) => {
    if (!rewardId) {
      setSelectedCopyFrom('');
      return;
    }

    setSelectedCopyFrom(rewardId);
    try {
      const reward = await rewardService.getRewardById(rewardId);
      if (!reward) {
        setError('Failed to load reward for copying');
        return;
      }

      // Copy all fields except those that should be unique or system-managed
      const copiedData: CreateRewardRequest = {
        reward_key: '', // User must provide a new unique key
        reward_name: reward.reward_name,
        ux_reward_title: reward.ux_reward_title,
        ux_reward_subtitle: reward.ux_reward_subtitle,
        ux_reward_description: reward.ux_reward_description,
        ux_image_url: reward.ux_image_url,
        category: reward.category,
        status: 'DRAFT', // New rewards start as DRAFT
        point_cost: reward.point_cost,
        dollar_value:
          typeof reward.dollar_value === 'string'
            ? parseFloat(reward.dollar_value)
            : reward.dollar_value,
        out_of_pocket_cost:
          typeof reward.out_of_pocket_cost === 'string'
            ? parseFloat(reward.out_of_pocket_cost)
            : reward.out_of_pocket_cost,
        ui_content_json: reward.ui_content_json,
        redemption_config: reward.redemption_config,
        reward_config: reward.reward_config,
        reward_variant: reward.reward_variant,
        provider_name: reward.provider_name,
      };

      setFormData(copiedData);
      setError(null);
    } catch (err) {
      setError('Failed to copy reward configuration');
      logger.error('Failed to copy reward', { error: err });
    }
  }, []);

  // Handle form field changes
  const handleChange = <K extends keyof CreateRewardRequest>(
    field: K,
    value: CreateRewardRequest[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle UI content JSON changes
  const handleUIContentChange = (content: WorkflowContent | null) => {
    setFormData((prev) => ({ ...prev, ui_content_json: content }));
  };

  // Handle redemption config changes
  const handleRedemptionConfigChange = (config: RedemptionConfig | null) => {
    setFormData((prev) => ({ ...prev, redemption_config: config }));
  };

  // Handle reward config changes
  const handleRewardConfigChange = (config: RewardConfig | null) => {
    setFormData((prev) => ({ ...prev, reward_config: config }));
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.reward_key?.trim()) {
      return 'Reward Key is required';
    }
    if (!formData.reward_name?.trim()) {
      return 'Internal Name is required';
    }
    if (!formData.ux_reward_title?.trim()) {
      return 'UX Title is required';
    }
    if (formData.point_cost < 0) {
      return 'Point Cost must be non-negative';
    }
    return null;
  };

  // Save changes
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await rewardService.createReward(formData);

      // Log record creation for audit
      auditService
        .logRecordModification('created', 'reward', created.reward_id, {
          reward_name: created.reward_name,
          category: created.category,
        })
        .catch((err) => logger.warn('Failed to log record creation', { error: err }));

      invalidate();
      refresh(); // Force immediate refetch
      navigate(`/rewards/${created.reward_id}`);
    } catch (err) {
      setError('Failed to create reward');
      logger.error('Failed to create reward', { error: err });
    } finally {
      setSaving(false);
    }
  };

  // Cancel
  const handleCancel = () => {
    navigate('/rewards');
  };

  return (
    <div className="reward-edit-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={handleCancel} className="btn-back-link">
          ← Cancel and go back
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-actions">
          <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating...' : 'Create Reward'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {/* Copy From Existing Reward */}
      <Card className="form-card copy-from-card">
        <CardHeader
          title="Copy from Existing Reward"
          subtitle="Optional: Start with an existing reward configuration"
        />
        <CardBody>
          <Select
            label="Select Reward to Copy"
            value={selectedCopyFrom}
            onChange={handleCopyFrom}
            options={[
              { value: '', label: 'Start from scratch' },
              ...availableRewards
                .sort((a, b) => a.reward_name.localeCompare(b.reward_name))
                .map((r) => {
                  const parts = [r.reward_name];
                  if (r.reward_variant && r.reward_variant !== 'default') {
                    parts.push(`[${r.reward_variant}]`);
                  }
                  if (r.provider_name) {
                    parts.push(`- ${r.provider_name}`);
                  }
                  return {
                    value: r.reward_id,
                    label: parts.join(' '),
                  };
                }),
            ]}
            disabled={loadingRewards}
            hint={
              selectedCopyFrom
                ? 'Configuration copied! Update the Reward Key to create a new reward.'
                : 'Select an existing reward to copy its configuration as a starting point.'
            }
          />
        </CardBody>
      </Card>

      {/* Basic Information */}
      <Card className="form-card">
        <CardHeader title="Basic Information" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Reward Key *"
              value={formData.reward_key}
              onChange={(val) => handleChange('reward_key', val)}
              placeholder="e.g., benepass-card"
              hint="Unique identifier for this reward"
            />
            <Input
              label="Internal Name *"
              value={formData.reward_name}
              onChange={(val) => handleChange('reward_name', val)}
              placeholder="e.g., Lore Rewards"
            />
            <SelectWithAdd
              label="Category *"
              value={formData.category}
              onChange={(val) => handleChange('category', val as RewardCategory)}
              options={categoryOptions.filter((o) => o.value !== '')}
              disabled={metadataLoading}
              onAddNew={(newCategory) => {
                // Add to local state if not already present
                if (!localCategories.includes(newCategory) && !categories.includes(newCategory)) {
                  setLocalCategories((prev) => [...prev, newCategory]);
                }
              }}
              addNewLabel="+ Add New Category"
              newItemPlaceholder="Enter new category name"
            />
            <Select
              label="Status"
              value={formData.status}
              onChange={(val) => handleChange('status', val as RewardStatus)}
              options={STATUS_OPTIONS.filter((o) => o.value !== '').map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <SelectWithAdd
              label="Variant"
              value={formData.reward_variant}
              onChange={(val) => handleChange('reward_variant', val as RewardVariant)}
              options={variantOptions.filter((o) => o.value !== '')}
              disabled={metadataLoading}
              onAddNew={(newVariant) => {
                // Add to local state if not already present
                if (!localVariants.includes(newVariant) && !variants.includes(newVariant)) {
                  setLocalVariants((prev) => [...prev, newVariant]);
                }
              }}
              addNewLabel="+ Add New Variant"
              newItemPlaceholder="Enter new variant name"
            />
            <SelectWithAdd
              label="Provider Name"
              value={formData.provider_name || ''}
              onChange={(val) => handleChange('provider_name', val || null)}
              options={providerOptions}
              disabled={loadingRewards}
              onAddNew={(newProvider) => {
                // Add to local state if not already present
                const existingProviders = availableRewards
                  .map((r) => r.provider_name)
                  .filter((p): p is string => !!p);
                if (
                  !localProviders.includes(newProvider) &&
                  !existingProviders.includes(newProvider)
                ) {
                  setLocalProviders((prev) => [...prev, newProvider]);
                }
              }}
              addNewLabel="+ Add New Provider"
              newItemPlaceholder="Enter new provider name"
              hint="Provider name (auto-synced from Redemption Config if not set)"
            />
            <Select
              label="Workflow Type"
              value={formData.ui_content_json?.workflow_type || ''}
              onChange={(val) => {
                if (!val) return;
                const currentContent = formData.ui_content_json || {
                  workflow_type: 'VARIABLE_AMOUNT' as WorkflowType,
                };
                handleUIContentChange({
                  ...currentContent,
                  workflow_type: val as WorkflowType,
                });
              }}
              options={WORKFLOW_OPTIONS}
            />
          </div>
        </CardBody>
      </Card>

      {/* Pricing */}
      <Card className="form-card">
        <CardHeader title="Pricing" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Point Cost *"
              value={formData.point_cost.toString()}
              onChange={(val) => handleChange('point_cost', val ? parseInt(val, 10) : 0)}
              type="number"
              hint="Cost in points to redeem"
            />
            <Input
              label="Dollar Value"
              value={formData.dollar_value?.toString() || ''}
              onChange={(val) => handleChange('dollar_value', val ? parseFloat(val) : null)}
              type="number"
              placeholder="Optional"
              hint="Dollar equivalent value"
            />
            <Input
              label="Out of Pocket Cost"
              value={formData.out_of_pocket_cost?.toString() || ''}
              onChange={(val) => handleChange('out_of_pocket_cost', val ? parseFloat(val) : null)}
              type="number"
              placeholder="Optional"
              hint="User's out of pocket cost"
            />
          </div>
        </CardBody>
      </Card>

      {/* Reward Configuration */}
      <Card className="form-card">
        <CardHeader
          title="Reward Configuration"
          subtitle="Limits, availability, pool membership, and completion points"
        />
        <CardBody>
          <RewardConfigEditor
            value={formData.reward_config ?? null}
            onChange={handleRewardConfigChange}
          />
        </CardBody>
      </Card>

      {/* Display Content */}
      <Card className="form-card">
        <CardHeader title="Display Content" subtitle="What users see in the app" />
        <CardBody>
          <div className="form-stack">
            <Input
              label="UX Title *"
              value={formData.ux_reward_title}
              onChange={(val) => handleChange('ux_reward_title', val)}
              placeholder="e.g., Transfer to Rewards Card"
            />
            <Input
              label="UX Subtitle"
              value={formData.ux_reward_subtitle || ''}
              onChange={(val) => handleChange('ux_reward_subtitle', val || null)}
              placeholder="e.g., Redeem up to $600 a year"
            />
            <TextArea
              label="UX Description"
              value={formData.ux_reward_description || ''}
              onChange={(val) => handleChange('ux_reward_description', val || null)}
              placeholder="e.g., (10 points = $1.00)"
              rows={2}
            />
            <Input
              label="Image URL"
              value={formData.ux_image_url || ''}
              onChange={(val) => handleChange('ux_image_url', val || null)}
              placeholder="Optional image URL"
            />
          </div>
        </CardBody>
      </Card>

      {/* UI Content JSON Editor */}
      <Card className="form-card ui-content-card">
        <CardHeader title="UI Configuration" subtitle="Advanced component and page configuration" />
        <CardBody>
          <UIContentEditor
            value={formData.ui_content_json ?? null}
            onChange={handleUIContentChange}
          />
        </CardBody>
      </Card>

      {/* Redemption Configuration */}
      <Card className="form-card">
        <CardHeader
          title="Redemption Configuration"
          subtitle="How this reward is fulfilled (manual, API, or instant)"
        />
        <CardBody>
          <RedemptionConfigEditor
            value={formData.redemption_config ?? null}
            onChange={handleRedemptionConfigChange}
          />
        </CardBody>
      </Card>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
          Cancel
        </button>
        <button className="btn-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating...' : 'Create Reward'}
        </button>
      </div>
    </div>
  );
};

export default RewardCreate;
